import classNames from 'classnames';
import React, { useState, useRef, useEffect } from 'react';
import Select, { components, DropdownIndicatorProps, GroupBase, OnChangeValue } from 'react-select';
import { RiCloseCircleLine } from 'react-icons/ri';
import { MdExpandMore, MdSearch } from 'react-icons/md';

import Spinner from '../../common/Spinner';

interface ShowSelectedNumberProps {
  isMultiple: boolean;
  values: DropdownOption[];
  searchLabel?: React.ReactNode;
}

interface DropdownOption {
  value: string | number;
  label?: string;
}

type SelectComponents = React.ComponentProps<
  typeof Select<DropdownOption, true, GroupBase<DropdownOption>>
>['components'];

const DropdownIndicator: React.FC<DropdownIndicatorProps<DropdownOption, true, GroupBase<DropdownOption>>> = (
  props
) => (
  <components.DropdownIndicator {...props}>
    <MdSearch className="height-16 width-16" />
  </components.DropdownIndicator>
);

const ShowSelectedNumber: React.FC<ShowSelectedNumberProps> = ({ isMultiple, values, searchLabel }) => {
  if (!values.length) {
    return (
      <div className="dropdown-search__select-text dropdown-search__select-none text-align-right">{searchLabel}</div>
    );
  }

  if (!isMultiple) return null;

  return (
    <span className="dropdown-search__select-text">
      {values.length}
      {' Selected'}
    </span>
  );
};

interface DropDownSearchProps {
  title?: string;
  onChange?: (selectedOptions: DropdownOption[]) => void;
  onRemove?: () => void;
  handleInputChange?: (inputValue: string) => void;
  noOptionsMessage?: () => React.ReactNode;
  isMultiple?: boolean;
  options?: DropdownOption[];
  isRemovable?: boolean;
  showSelected?: boolean;
  values: DropdownOption[];
  searchLabel?: React.ReactNode;
  isLoading?: boolean;
  className?: string;
  components?: SelectComponents;
  iconComponent?: (option: DropdownOption) => React.ReactNode;
  isDebounceLoading?: boolean;
}

const DropDownSearch: React.FC<DropDownSearchProps> = ({
  title,
  onChange,
  onRemove,
  handleInputChange,
  noOptionsMessage,
  isMultiple = true,
  options,
  isRemovable = false,
  showSelected = false,
  values,
  searchLabel,
  isLoading,
  className = '',
  components: selectComponents = { DropdownIndicator },
  iconComponent,
  isDebounceLoading,
}) => {
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsSelectOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (selectedOptions: OnChangeValue<DropdownOption, true>) => {
    onChange?.(selectedOptions ? [...selectedOptions] : []);
  };

  return (
    <div className={classNames('dropdown-search', { 'dropdown-search--active': isSelectOpen })} ref={wrapperRef}>
      <button
        className={`dropdown-search__control dropdown-search__btn-max d-flex justify-content-between ${className}`}
        onClick={() => setIsSelectOpen((prev) => !prev)}
      >
        <div className="d-flex flex-column">
          {showSelected && <div className="text-align-left dropdown-search__title">{title}</div>}
          <div className="dropdown-search__control--label d-flex text-align-left">
            {!showSelected && title}
            {showSelected && (
              <ShowSelectedNumber
                isMultiple={isMultiple}
                values={values}
                searchLabel={isLoading ? <Spinner className="dropdown-search__loading" /> : searchLabel}
              />
            )}
            <div
              className={classNames({
                'dropdown-search__control__active-icon': isSelectOpen,
                'dropdown-search__control__icon': !isSelectOpen,
              })}
            >
              <MdExpandMore aria-label="Options" />
            </div>
          </div>
        </div>
      </button>

      {isRemovable && (
        <div className="dropdown-search__remove" title="Remove" onClick={() => onRemove?.()}>
          <RiCloseCircleLine aria-label="Remove dropdown" />
        </div>
      )}

      {isSelectOpen && (
        <div className="dropdown-search__select">
          <Select
            autoFocus
            menuIsOpen
            isClearable
            isSearchable
            isLoading={isDebounceLoading}
            value={values}
            isMulti={isMultiple}
            closeMenuOnSelect={false}
            options={options}
            components={selectComponents}
            placeholder={`Search ${title}...`}
            classNamePrefix="dropdown-search__select"
            className="dropdown-search__select__container"
            onChange={handleChange}
            onInputChange={(value) => {
              handleInputChange?.(value);
              return value;
            }}
            noOptionsMessage={noOptionsMessage}
            formatOptionLabel={iconComponent}
          />
        </div>
      )}
    </div>
  );
};

export default DropDownSearch;
